import { create } from "zustand"
import { persist } from "zustand/middleware"

import type { ScholarState } from "@/api/models"

import i18n from "@/languageConfig"
import { uiOption } from "@/lib/ui-options"

import { createNamespacedStore } from "./store-factory"

export type ResearchPreferences = {
  reportLength: "short" | "standard" | "extended" | "custom"
  researchDepth: "dev" | "quick" | "detailed"
  language: string
  availableLanguages: string[]
  reportFormatText: string
  reportFormatFile: File | null
  reportFormatSource: "structured_summary" | "standard" | "file" | "message"
  customReportLength: number
  sources: ("web" | "company_libraries" | "user_documents")[]
  countryCodes: string
  sourceUrls: string
  fileHandlingStrategy: "search_in_files" | "use_files_as_context"
  trustedMode: "only" | "preferred"
}

export const sanitizeResearchDepth = (
  value?: string | null
): ResearchPreferences["researchDepth"] => {
  // Opt-in matches uiOption: a stored boolean wins, otherwise build-time isDev === true.
  if (value === "dev") {
    return uiOption("show_dev_options") ? "dev" : "detailed"
  }
  if (value === "quick" || value === "detailed") {
    return value
  }
  return "detailed"
}

// Every language in `supportedLngs` (languageConfig.ts) needs an entry, or a
// user on that UI language silently gets English output (#2968).
const CODE_TO_DISPLAY_LANGUAGE: Record<string, string> = {
  en: "English",
  sv: "Svenska",
  no: "Norsk",
  fi: "Suomi",
  de: "Deutsch",
}

const DEFAULT_AVAILABLE_LANGUAGES = ["Svenska", "English", "Norsk", "Suomi", "Deutsch"]

const getDefaultLanguage = () => {
  const code = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0]
  return CODE_TO_DISPLAY_LANGUAGE[code] ?? "English"
}

const getDefaultPreferences = (): ResearchPreferences => ({
  reportLength: "standard",
  researchDepth: "detailed",
  language: getDefaultLanguage(),
  availableLanguages: [...DEFAULT_AVAILABLE_LANGUAGES],
  reportFormatText: "",
  reportFormatFile: null,
  reportFormatSource: "structured_summary",
  customReportLength: 2000,
  sources: ["web", "company_libraries"],
  countryCodes: "",
  sourceUrls: "",
  fileHandlingStrategy: "use_files_as_context",
  trustedMode: "only",
})

type ResearchPreferencesState = {
  prefs: ResearchPreferences
  setReportLength: (val: ResearchPreferences["reportLength"]) => void
  setCustomReportLength: (val: number) => void
  setResearchDepth: (val: ResearchPreferences["researchDepth"]) => void
  setLanguage: (val: string) => void
  addLanguage: (language: string) => void
  deleteLanguage: (language: string) => void
  setReportFormatText: (val: string) => void
  setReportFormatFile: (file: File | null) => void
  setReportFormatSource: (val: ResearchPreferences["reportFormatSource"]) => void
  setSources: (val: ResearchPreferences["sources"]) => void
  setCountryCodes: (val: string) => void
  setSourceUrls: (val: string) => void
  setFileHandlingStrategy: (val: ResearchPreferences["fileHandlingStrategy"]) => void
  setTrustedMode: (val: ResearchPreferences["trustedMode"]) => void
  updateFromAgentState: (agentState?: ScholarState) => void
  resetPreferences: () => void
  resetAdvancedSettings: () => void
}

/**
 * Factory function to create a new Research Preferences store instance.
 * Includes persist middleware for language preferences.
 */
function createResearchPreferencesStoreInstance(sessionType: string) {
  return create<ResearchPreferencesState>()(
    persist(
      (set) => ({
        prefs: getDefaultPreferences(),
        setReportLength: (val) =>
          set((state) => ({ prefs: { ...state.prefs, reportLength: val } })),
        setCustomReportLength: (val) =>
          set((state) => ({
            prefs: { ...state.prefs, customReportLength: val },
          })),
        setResearchDepth: (val) =>
          set((state) => ({
            prefs: { ...state.prefs, researchDepth: sanitizeResearchDepth(val) },
          })),
        setLanguage: (val) => set((state) => ({ prefs: { ...state.prefs, language: val } })),
        addLanguage: (language) =>
          set((state) => {
            const exists = state.prefs.availableLanguages
              .map((l) => l.toLowerCase())
              .includes(language.trim().toLowerCase())
            if (exists) return state
            return {
              prefs: {
                ...state.prefs,
                availableLanguages: [...state.prefs.availableLanguages, language],
              },
            }
          }),
        deleteLanguage: (language) =>
          set((state) => {
            const newAvailable = state.prefs.availableLanguages.filter((l) => l !== language)
            const isDeletingSelected = state.prefs.language.toLowerCase() === language.toLowerCase()
            let nextLanguage = state.prefs.language
            if (isDeletingSelected) {
              nextLanguage = newAvailable[0] ? newAvailable[0] : state.prefs.language
            }
            return {
              prefs: {
                ...state.prefs,
                availableLanguages: newAvailable,
                language: nextLanguage,
              },
            }
          }),
        setReportFormatText: (val) =>
          set((state) => ({
            prefs: { ...state.prefs, reportFormatText: val, reportFormatFile: null },
          })),
        setReportFormatFile: (file) =>
          set((state) => ({
            prefs: { ...state.prefs, reportFormatFile: file, reportFormatText: "" },
          })),
        setReportFormatSource: (val) =>
          set((state) => ({
            prefs: { ...state.prefs, reportFormatSource: val },
          })),
        setSources: (val) =>
          set((state) => ({
            prefs: { ...state.prefs, sources: val },
          })),
        setCountryCodes: (val) =>
          set((state) => ({
            prefs: { ...state.prefs, countryCodes: val },
          })),
        setSourceUrls: (val) =>
          set((state) => ({
            prefs: { ...state.prefs, sourceUrls: val },
          })),
        setFileHandlingStrategy: (val) =>
          set((state) => ({
            prefs: { ...state.prefs, fileHandlingStrategy: val },
          })),
        setTrustedMode: (val) =>
          set((state) => ({
            prefs: { ...state.prefs, trustedMode: val },
          })),
        updateFromAgentState: (agentState) => {
          if (!agentState) return
          const rp = agentState.research_preferences
          if (!rp) return
          set((state) => {
            let newSources = state.prefs.sources
            if (Array.isArray(rp.USE_SOURCES)) {
              const sourceMapping: Record<number, string> = {
                0: "web",
                1: "company_libraries",
                2: "user_documents",
              }
              const serverSources = rp.USE_SOURCES.map(
                (source: number) => sourceMapping[source]
              ).filter(Boolean) as ResearchPreferences["sources"]
              if (serverSources.length > 0) {
                newSources = serverSources
              }
            }

            return {
              prefs: {
                ...state.prefs,
                reportLength: rp.REPORT_LENGTH
                  ? (rp.REPORT_LENGTH as ResearchPreferences["reportLength"])
                  : state.prefs.reportLength,
                customReportLength:
                  rp.REPORT_LENGTH === "custom" && rp.TOTAL_WORDS
                    ? (rp.TOTAL_WORDS as number)
                    : state.prefs.customReportLength,
                researchDepth: rp.RESEARCH_DEPTH
                  ? sanitizeResearchDepth(rp.RESEARCH_DEPTH as string)
                  : state.prefs.researchDepth,
                reportFormatText:
                  rp.REPORT_FORMAT_SOURCE === "message" && rp.REPORT_FORMAT
                    ? (rp.REPORT_FORMAT as string)
                    : state.prefs.reportFormatText,
                reportFormatSource: rp.REPORT_FORMAT_SOURCE
                  ? (rp.REPORT_FORMAT_SOURCE as ResearchPreferences["reportFormatSource"])
                  : state.prefs.reportFormatSource,
                sources: newSources,
                countryCodes: Array.isArray(rp.COUNTRY_CODES)
                  ? (rp.COUNTRY_CODES as string[]).join(", ")
                  : state.prefs.countryCodes,
                sourceUrls: Array.isArray(rp.SOURCE_URLS)
                  ? (rp.SOURCE_URLS as string[]).join("\n")
                  : state.prefs.sourceUrls,
                fileHandlingStrategy: rp.FILE_HANDLING_STRATEGY
                  ? (rp.FILE_HANDLING_STRATEGY as ResearchPreferences["fileHandlingStrategy"])
                  : state.prefs.fileHandlingStrategy,
              },
            }
          })
        },
        resetPreferences: () =>
          set({
            prefs: getDefaultPreferences(),
          }),
        resetAdvancedSettings: () =>
          set((state) => ({
            prefs: {
              ...state.prefs,
              sources: getDefaultPreferences().sources,
              countryCodes: "",
              sourceUrls: "",
              fileHandlingStrategy: "use_files_as_context",
              trustedMode: state.prefs.trustedMode,
            },
          })),
      }),
      {
        name: `research-preferences-${sessionType}`,
        version: 1,
        migrate: (persistedState, version) => {
          if (version < 1) {
            const incomingState = persistedState as Partial<ResearchPreferencesState>
            const incomingPrefs = incomingState.prefs as Partial<ResearchPreferences> | undefined
            const language =
              typeof incomingPrefs?.language === "string" &&
              incomingPrefs.language.trim().length > 0
                ? incomingPrefs.language
                : undefined
            return language ? { prefs: { language } } : {}
          }
          return persistedState
        },
        partialize: (state) => ({ prefs: { language: state.prefs.language } }),
        merge: (persistedState, currentState) => {
          const incoming = (persistedState as Partial<ResearchPreferencesState>).prefs ?? {}
          const incomingPrefs = incoming as Partial<ResearchPreferences>
          const nextLanguage =
            typeof incomingPrefs.language === "string" && incomingPrefs.language.trim().length > 0
              ? incomingPrefs.language
              : currentState.prefs.language
          return {
            ...currentState,
            prefs: {
              ...currentState.prefs,
              language: nextLanguage,
            },
          }
        },
      }
    )
  )
}

/**
 * Hook to get Research Preferences store isolated by sessionType.
 * Each sessionType gets its own store instance with isolated preferences.
 *
 * @param sessionType - Session type identifier (e.g., "variant-a", "variant-b")
 * @returns Zustand store hook for the specific sessionType
 */
export function useResearchPreferencesStore(sessionType: string) {
  return createNamespacedStore("research-preferences", sessionType, () =>
    createResearchPreferencesStoreInstance(sessionType)
  )
}
